<template>
  <div class="st-surface">
    <StToolbar tone="canvas" class="st-surface__bar">
      <span class="st-surface__title">Rules Documents</span>
      <span class="st-toolbar-spacer" />
      <FormattingHelp />
    </StToolbar>

    <div class="st-surface__body">
      <!-- Sidebar: document list (shared rail; reorderable + resizable) -->
      <EditorSideList
        :items="ws.rulesDocuments.value"
        :selected-index="selectedIndex"
        add-label="Add Document"
        empty-hint="No documents yet."
        width-key="studio:rulesSidebarWidth"
        @select="selectedIndex = $event"
        @add="addDocument"
        @reorder="moveDocument"
      >
        <template #row="{ item }">{{ item.title || '(untitled)' }}</template>
      </EditorSideList>

      <div v-if="!currentDoc" class="st-surface__placeholder">
        Select or add a document to begin editing.
      </div>

      <template v-else>
        <!-- Second rail: the document outline (its top-level sections) -->
        <EditorSideList
          :items="currentDoc.sections"
          :selected-index="selectedSectionIndex"
          add-label="Add Section"
          empty-hint="No sections yet."
          width-key="studio:rulesOutlineWidth"
          show-index
          @select="selectedSectionIndex = $event"
          @add="addSection"
          @reorder="moveSection"
        >
          <template #row="{ item }">{{ item.name || '(untitled section)' }}</template>
        </EditorSideList>

        <!-- Editor card: doc header + the active section / preview -->
        <main class="st-surface__main">
          <div class="st-editor-card">
            <TitleBar
              :name="currentDoc.title"
              noun="Document"
              @rename="renameDocument"
              @duplicate="duplicateDocument"
              @delete="deleteDocument"
            >
              <template #tabs>
                <StSegmentedControl v-model="currentTab" :options="tabOptions" size="sm" on-shell />
              </template>
            </TitleBar>

            <!-- Editor tab: edit the selected section -->
            <div
              v-if="currentTab === 'editor'"
              class="st-editor-card__pane st-editor-card__pane--pad"
            >
              <RuleSectionEditor
                v-if="currentSection"
                :section="currentSection"
                @update:section="updateSelectedSection"
                @delete="removeSection"
              />
              <p v-else class="rules-editor__hint">
                Select a section from the outline, or add one to begin editing.
              </p>
            </div>

            <!-- Preview tab: the selected section, or the whole document -->
            <div v-else class="st-editor-card__pane st-editor-card__pane--pad">
              <div class="rules-editor__scope">
                <StSegmentedControl v-model="previewScope" :options="scopeOptions" size="sm" />
              </div>
              <RulesDocumentView
                v-if="previewScope === 'document'"
                :document="currentDoc"
                :bundle="ws.snapshotBundle()"
              />
              <template v-else>
                <RuleSectionView
                  v-for="(section, i) in sectionPreviewSections"
                  :key="i"
                  :section="section"
                />
                <p v-if="!sectionPreviewSections.length" class="rules-editor__hint">
                  Select a section to preview it.
                </p>
              </template>
            </div>
          </div>
        </main>
      </template>
    </div>

    <NameModal
      :open="addingDoc"
      title="New Document"
      placeholder="Document name…"
      @confirm="handleAddDocument"
      @close="addingDoc = false"
    />
    <NameModal
      :open="addingSection"
      title="New Section"
      placeholder="Section name…"
      @confirm="handleAddSection"
      @close="addingSection = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  ModeOfPlay,
  expandRuleDocument,
  type RuleDocument,
  type RuleSection,
} from '@scrolldex/shared';
import { StToolbar, StSegmentedControl, type SegmentOption } from '../components/ui/index.js';
import EditorSideList from '../components/EditorSideList.vue';
import TitleBar from '../components/editor/TitleBar.vue';
import RuleSectionEditor from '../components/rules/RuleSectionEditor.vue';
import RuleSectionView from '../components/rules/RuleSectionView.vue';
import RulesDocumentView from '../components/rules/RulesDocumentView.vue';
import FormattingHelp from '../components/FormattingHelp.vue';
import NameModal from '../components/NameModal.vue';
import { useWorkspace } from '../state/workspace.js';
import { uniqueName } from '../utils/uniqueName.js';
import { useEntityTabs } from '../composables/useEntityTabs.js';
import { useIndexedCollection } from '../composables/useIndexedCollection.js';

const ws = useWorkspace();

const addingDoc = ref(false);
const addingSection = ref(false);
const documents = useIndexedCollection<RuleDocument>({
  items: () => ws.rulesDocuments.value,
  setItems: (next) => ws.setRulesDocuments(next),
});
const {
  selectedIndex,
  current: currentDoc,
  append: appendDocument,
  updateCurrent: updateCurrentDocument,
  deleteCurrent: deleteCurrentDocument,
  insertAfterCurrent: insertAfterCurrentDocument,
  move: moveRuleDocument,
} = documents;

const tabOptions: SegmentOption[] = [
  { value: 'editor', label: 'Editor', icon: 'file-pen' },
  { value: 'preview', label: 'Preview', icon: 'eye' },
];

const scopeOptions: SegmentOption[] = [
  { value: 'section', label: 'Section', icon: 'align-left' },
  { value: 'document', label: 'Document', icon: 'book-bookmark' },
];
const previewScope = ref<'section' | 'document'>('section');

// Per-document tab memory keyed by document title (migrated on rename).
const {
  currentTab,
  setTab,
  migrate: migrateTab,
} = useEntityTabs<'editor' | 'preview'>(() => currentDoc.value?.title, 'editor');

function addDocument() {
  addingDoc.value = true;
}

function handleAddDocument(title: string) {
  const newDoc: RuleDocument = {
    title,
    modeOfPlay: ModeOfPlay.Any,
    battlePack: (ws.battlePack.value ?? undefined) as RuleDocument['battlePack'],
    sections: [],
  };
  appendDocument(newDoc);
  setTab(newDoc.title, 'editor');
  addingDoc.value = false;
}

function deleteDocument() {
  deleteCurrentDocument();
}

function duplicateDocument() {
  const docs = ws.rulesDocuments.value;
  const doc = currentDoc.value;
  if (!doc) return;

  // Deep-copy to avoid shared section references
  const copy: RuleDocument = JSON.parse(JSON.stringify(doc));
  copy.title = uniqueName(
    doc.title,
    docs.map((d) => d.title),
  );

  insertAfterCurrentDocument(copy);
  setTab(copy.title, 'editor');
}

function moveDocument(from: number, to: number) {
  moveRuleDocument(from, to);
}

function updateCurrentDoc(updated: RuleDocument) {
  const oldTitle = currentDoc.value?.title ?? '';
  updateCurrentDocument(updated);
  // Migrate tab memory if title changed
  if (updated.title !== oldTitle) migrateTab(oldTitle, updated.title);
}

function renameDocument(title: string) {
  if (!currentDoc.value) return;
  updateCurrentDoc({ ...currentDoc.value, title });
}

// ─── Sections (outline) ───────────────────────────────────────────────────────
const sections = useIndexedCollection<RuleSection>({
  items: () => currentDoc.value?.sections ?? [],
  setItems: (next) => setSections(next),
});
const {
  selectedIndex: selectedSectionIndex,
  list: currentSections,
  current: currentSection,
  append: appendSection,
  updateCurrent: updateCurrentSection,
  deleteCurrent: deleteCurrentSection,
  move: moveRuleSection,
} = sections;

watch(selectedIndex, () => {
  selectedSectionIndex.value = 0;
});

function setSections(sections: RuleSection[]) {
  if (!currentDoc.value) return;
  updateCurrentDoc({ ...currentDoc.value, sections });
}

function newSection(name: string): RuleSection {
  return { name, modeOfPlay: ModeOfPlay.Any, subSections: [], containers: [] };
}

function addSection() {
  addingSection.value = true;
}

function handleAddSection(name: string) {
  appendSection(newSection(name));
  addingSection.value = false;
}

function removeSection() {
  deleteCurrentSection();
}

function moveSection(from: number, to: number) {
  moveRuleSection(from, to);
}

function updateSelectedSection(updated: RuleSection) {
  updateCurrentSection(updated);
}

// Single-section preview: expand just the selected section against the bundle so
// any `ref` markers (battleplans/battleTactics) resolve the same as in the full doc.
const sectionPreviewSections = computed<RuleSection[]>(() => {
  const doc = currentDoc.value;
  const section = currentSection.value;
  if (!doc || !section) return [];
  return expandRuleDocument({ ...doc, sections: [section] }, ws.snapshotBundle()).sections;
});
</script>

<style scoped>
.rules-editor__scope {
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--st-space-3);
}
.rules-editor__hint {
  margin: 0;
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
  font-style: italic;
}
</style>
